import React, { useState, useEffect, useRef } from 'react';
import GeneralContext from './GeneralContext';
import { useCookies } from 'react-cookie';
import axios from '../../api/axios';
import { auth } from '../../hooks/firebase';
import { ROL_ADMIN } from '../../Constants/Constants';

const STORAGE_KEY = 'PrefacturaBannerOpen'; 

const ContextProvider = ({ children }) => {
  const [pageTitle, setPageTitle] = useState("");
  const [drawerHidding, setDrawerHidding] = useState(true);

  // Usuario original desde backend (sin overrides)
  const [serverUser, setServerUser] = useState(null);
  // Sesión efectiva (aplica overrides de identidad)
  const [userSesion, setUserSesion] = useState(null);

  const [cookies, setCookie, removeCookie] = useCookies(['sagec_access_token']);
  const [userResources, setUserResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const refreshInterval = useRef(null);

  // Forzar remount del contenido
  const [contentKey, setContentKey] = useState(0);
  const reloadChildren = () => setContentKey(k => k + 1);


  const [roleOverrideId, setRoleOverrideId] = useState(null);
  const [identityOverride, setIdentityOverride] = useState({ value: null }); 

  const changeEffectiveRole = (roleIdOrNull) => {
    if (roleIdOrNull === null || roleIdOrNull === '' || Number.isNaN(Number(roleIdOrNull))) {
      setRoleOverrideId(null);
    } else {
      setRoleOverrideId(Number(roleIdOrNull));
    }
  };


  // Llama a getOperator solo si hay rol 3/4 y no existe ya un nombre previo
const ensureOperadorInfo = async (
  roles,
  idOrRut,
  prevNombre,
  prevIdOperador
) => {
  // Si ya tengo ambos, no hago nada
  if (prevNombre && prevIdOperador) {
    return { nombre_operador: prevNombre, id_operador: prevIdOperador };
  }

  const hasOpRole =
    Array.isArray(roles) && roles.some((r) => r?.role_id === 3 || r?.role_id === 4);

  if (!hasOpRole || !idOrRut) {
    return { nombre_operador: null, id_operador: null };
  }

  try {
    const opResp = await axios.getOperator(idOrRut);
    const data = opResp?.data ?? {};

    return {
      nombre_operador: typeof data.operador === "string" ? data.operador : null,
      id_operador: data.id_operador ?? null,
    };
  } catch {
    return { nombre_operador: null, id_operador: null };
  }
};



const updateUserIdentity = (patch = {}) => {
  // 1) Determinar valor de identidad (mantengo tu lógica)
  const raw = typeof patch.rut !== 'undefined' ? patch.rut : patch.id;
  const value = raw ?? null;
  setIdentityOverride({ value });

  // 2) Eliminar claves con "undefined" para no sobreescribir con undefined
  const cleanPatch = Object.fromEntries(
    Object.entries(patch).filter(([, v]) => v !== undefined)
  );

  // 3) Merge inmutable manteniendo keys no incluidas en patch (y respetando siempre lo anterior)
  setUserSesion(prev => (prev ? { ...prev, ...cleanPatch } : { ...cleanPatch }));

  // 4) Actualizar recursos (mantengo tu fuente)
  const rolesSource = (cleanPatch.roles ?? cleanPatch.roles) || [];
  const combinedResources = rolesSource.flatMap(role =>
    (role.recursos || []).map(resource => resource.nombre_recurso)
  );
  setUserResources(combinedResources);
  console.log(combinedResources);

  // Resolución de nombre_operador en segundo plano (no cambio sync de tu función)
  const idForOp =
    cleanPatch.id ?? cleanPatch.rut ?? userSesion?.id ?? userSesion?.rut ?? null;

  ensureOperadorInfo(
    rolesSource,
    idForOp,
    userSesion?.nombre_operador,
    userSesion?.id_operador
  ).then((info) => {
    const nombre = info?.nombre_operador;
    const idOp = info?.id_operador;

    if (nombre || idOp) {
      setUserSesion((prev) => {
        if (!prev) return prev;

        const next = { ...prev };

        // no pisar si ya existen
        if (!next.nombre_operador && nombre) next.nombre_operador = nombre;
        if (!next.id_operador && idOp) next.id_operador = idOp;

        return next;
      });
    }
  });

  return { ok: true, value };
};



  const resetUserIdentityOverrides = () => {
    setIdentityOverride({ value: null });
    setUserSesion(serverUser); 
  };

  // ====== Banner (persistencia permitida) ======
  const saveBannerState = (open) => localStorage.setItem(STORAGE_KEY, JSON.stringify(open));
  const openPrefacturaBanner  = () => { setPrefacturaBannerOpen(true);  saveBannerState(true); };
  const closePrefacturaBanner = () => { setPrefacturaBannerOpen(false); saveBannerState(false); };
  const initialBannerState = (() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === null ? true : JSON.parse(saved);
  })();
  const [prefacturaBannerOpen, setPrefacturaBannerOpen] = useState(initialBannerState);

  // ====== Auth ======
  const login = (token) => {
    setCookie('sagec_access_token', token, {
      path: '/',
      maxAge: 3600,
      secure: true,
      sameSite: 'None',
    });
    openPrefacturaBanner();
  };

  const logout = () => {
    if (refreshInterval.current) {
      clearInterval(refreshInterval.current);
      refreshInterval.current = null;
    }
    removeCookie('sagec_access_token', { path: '/' });
    setServerUser(null);
    setUserSesion(null);

    // limpiar overrides en memoria
    setRoleOverrideId(null);
    setIdentityOverride({ value: null });

    window.location.href = 'https://accounts.claveunica.gob.cl/api/v1/accounts/app/logout?redirect=/';
  };

  const getToken = () => cookies.sagec_access_token;
  const isAuthenticated = () => !!cookies.sagec_access_token;

  // ====== Cálculo de roles ======

  // EFECTIVO (respeta override)
  const getEffectiveRoleIds = () => {
    if (roleOverrideId !== null) return [roleOverrideId];
    return userSesion?.roles?.map(r => r.role_id) ?? [];
  };

  // ORIGINAL (siempre los del backend)
  const getOriginalRoleIds = () => {
    return serverUser?.roles?.map(r => r.role_id) ?? [];
  };

  // includeRol EFECTIVO (respeta override)
  const includeRol = (roles) => {
    const rolesArray = Array.isArray(roles) ? roles : [roles];
    const effectiveIds = getEffectiveRoleIds();
    return rolesArray.some(element => effectiveIds.includes(element));
  };

  // includeRol ORIGINAL (ignora override)
  const includeRolOriginal = (roles) => {
    const rolesArray = Array.isArray(roles) ? roles : [roles];
    const originalIds = getOriginalRoleIds();
    return rolesArray.some(element => originalIds.includes(element));
  };

  const isAdmin = includeRolOriginal([ROL_ADMIN]);

  // ====== Overrides de identidad sobre un user base ======
  const applyIdentityOverrides = (baseUser) => {
    if (!baseUser) return baseUser;
    const v = identityOverride.value;
    if (!v) return baseUser;
    return {
      ...baseUser,
      id: v,
      rut: v,
    };
  };

  // ====== Carga de usuario ======
const getUser = async () => {
  axios.getUserLogin(cookies.sagec_access_token)
    .then((response) => {
      const combinedResources = response.data.roles.flatMap(role => role.recursos.map(resource => resource.nombre_recurso));

      // base desde API; si no viene id, usamos rut
      const base = {
        ...response.data,
        id: response.data.id ?? response.data.rut ?? '',
        rut: response.data.rut ?? response.data.id ?? '',
        nombre: `${response.data.nombre} ${response.data.apellidos}`,
      };

      setServerUser(base); // original del backend

      const effective = applyIdentityOverrides(base);

      // primero mergeamos la sesión respetando SIEMPRE lo anterior
      setUserSesion(prev => (prev ? { ...prev, ...effective } : { ...effective }));

      // luego, en segundo plano, resolvemos nombre_operador solo si aplica (rol 3/4)
      ensureOperadorInfo(
            response.data.roles,
            base.id,
            userSesion?.nombre_operador,
            userSesion?.id_operador
          ).then((info) => {
            const nombre = info?.nombre_operador;
            const idOp = info?.id_operador;

            if (nombre || idOp) {
              setUserSesion((prev) => {
                if (!prev) return prev;

                const next = { ...prev };
                if (!next.nombre_operador && nombre) next.nombre_operador = nombre;
                if (!next.id_operador && idOp) next.id_operador = idOp;

                return next;
              });
            }
          });


      setUserResources(combinedResources);
    })
    .catch(() => {
      logout();
    })
    .finally(() => setLoading(false));
};


  const refresh = async () => {
    try {
      const user = auth.currentUser;
      if (user && cookies.sagec_access_token) {
        const newToken = await user.getIdToken(true);
        if (cookies.sagec_access_token) {
          setCookie('sagec_access_token', newToken, {
            path: '/',
            maxAge: 3600,
            secure: false,
            sameSite: 'None',
          });
        }
        return newToken;
      } else {
        logout();
      }
    } catch (error) {
      console.error("Error al refrescar el token con Firebase:", error);
      logout();
    }
  };

  // ====== Efectos ======
  useEffect(() => {
    if (cookies.sagec_access_token) {
      getUser();
    }

    if (cookies.sagec_access_token && !refreshInterval.current) {
      getUser();
      refreshInterval.current = setInterval(() => {
        refresh();
      }, 600000); 
    }

    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
        refreshInterval.current = null;
      }
    };
  }, [cookies.sagec_access_token]);

  useEffect(() => {
    if (serverUser) setUserSesion(applyIdentityOverrides(serverUser));
  }, [serverUser]);


  return (
    <GeneralContext.Provider value={{
      pageTitle, setPageTitle,
      drawerHidding, setDrawerHidding,

      userSesion, setUserSesion, serverUser,
      login, logout, getToken, isAuthenticated,

      // Permisos
      includeRol,
      isAdmin,

      userResources,
      loading,

      // Banner
      prefacturaBannerOpen,
      openPrefacturaBanner,
      closePrefacturaBanner,

      // Overrides en memoria
      effectiveRoleId: roleOverrideId,
      changeEffectiveRole,

      // Identidad (RUT/ID): un único valor
      updateUserIdentity,
      resetUserIdentityOverrides,

      // Recarga de contenido
      contentKey,
      reloadChildren,
    }}>
      {children}
    </GeneralContext.Provider>
  );
};

export default ContextProvider;
